import queryString from "query-string";
import { toJS } from "mobx";
import { RouteType, RouteParamsType, RouteQueryParamsType, ParamValuesType } from "types/RouteType";

/**
 * Handles URL parsing, generation, and parameter extraction.
 * Follows Single Responsibility Principle by focusing only on URL operations.
 */
export class UrlParser {
    private debugMode = false;

    constructor(debugMode: boolean = false) {
        this.debugMode = debugMode;
    }

    /**
     * Parses a URL and extracts route information
     * @param url - Full URL to parse
     * @param routes - Available routes to match against
     * @returns Route information or null if no match found
     */
    parseUrl(url: string, routes: { [key: string]: RouteType }): {
        route: RouteType;
        params: RouteParamsType;
        queryParams: RouteQueryParamsType;
    } | null {
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const queryParams = this.parseQueryString(urlObj.search);

            // Find matching route
            for (const route of Object.values(routes)) {
                const match = this.matchRouteToPath(route, pathname);
                if (match) {
                    return {
                        route,
                        params: match.params,
                        queryParams
                    };
                }
            }

            this.debugLog(`No route found for path: ${pathname}`);
            return null;
        } catch (error) {
            this.debugLog(`Error parsing URL: ${url}`, error);
            return null;
        }
    }

    /**
     * Matches a route pattern to a URL path and extracts parameters
     * @param route - Route to match against
     * @param path - URL path to match
     * @returns Match result with parameters or null if no match
     */
    private matchRouteToPath(route: RouteType, path: string): { params: RouteParamsType } | null {
        const routePath = (route as RouteType & { originalPath?: string }).originalPath || route.path;
        const pathSegments = path.split('/').filter(segment => segment.length > 0);
        const routeSegments = routePath.split('/').filter((segment: string) => segment.length > 0);

        // Handle optional parameters
        const minSegments = routeSegments.filter((segment: string) => !segment.includes('?')).length;
        const maxSegments = routeSegments.length;

        if (pathSegments.length < minSegments || pathSegments.length > maxSegments) {
            return null;
        }

        const params: RouteParamsType = {};

        for (let i = 0; i < routeSegments.length && i < pathSegments.length; i++) {
            const routeSegment = routeSegments[i];
            const pathSegment = pathSegments[i];

            if (routeSegment.startsWith(':')) {
                // Parameter segment
                const paramName = routeSegment.replace(':', '').replace('?', '');
                params[paramName] = decodeURIComponent(pathSegment);
            } else if (routeSegment !== pathSegment) {
                // Static segment mismatch
                return null;
            }
        }

        return { params };
    }

    /**
     * Generates a URL from route information
     * @param route - Route to generate URL for
     * @param params - Parameters to include in the URL
     * @param queryParams - Query parameters to include
     * @returns Generated URL string
     */
    generateUrl(
        route: RouteType,
        params?: RouteParamsType,
        queryParams?: RouteQueryParamsType
    ): string {
        let url = (route as RouteType & { originalPath?: string }).originalPath || route.path;

        // Replace route parameters
        if (params) {
            const jsParams = toJS(params);
            for (const [key, value] of Object.entries(jsParams)) {
                if (value !== undefined && value !== null) {
                    url = url.replace(`:${key}?`, String(value));
                    url = url.replace(`:${key}`, String(value));
                } else {
                    // Remove optional parameters that are not provided
                    url = url.replace(`/:${key}?`, '');
                }
            }
        }

        // Remove any remaining optional parameters
        url = url.replace(/\/:[^/?]*\?/g, '');

        // Add query parameters
        if (queryParams && Object.keys(queryParams).length > 0) {
            const queryString = this.buildQueryString(queryParams);
            url += `?${queryString}`;
        }

        return url;
    }

    /**
     * Parses query string into object
     * @param queryStr - Query string to parse (with or without leading ?)
     * @returns Object with query parameters
     */
    parseQueryString(queryStr: string): RouteQueryParamsType {
        if (!queryStr) {
            return {};
        }

        // Remove leading ? if present
        const cleanQuery = queryStr.startsWith('?') ? queryStr.slice(1) : queryStr;

        try {
            return queryString.parse(cleanQuery) as RouteQueryParamsType;
        } catch (error) {
            this.debugLog('Error parsing query string:', error);
            return {};
        }
    }

    /**
     * Builds query string from object
     * @param queryParams - Object with query parameters
     * @returns Query string (without leading ?)
     */
    buildQueryString(queryParams: RouteQueryParamsType): string {
        try {
            const jsParams = toJS(queryParams);
            // Filter out undefined and null values
            const cleanParams = Object.fromEntries(
                Object.entries(jsParams).filter(([_, value]) => value !== undefined && value !== null)
            );
            return queryString.stringify(cleanParams);
        } catch (error) {
            this.debugLog('Error building query string:', error);
            return '';
        }
    }

    /**
     * Extracts parameters from a URL path using a route template
     * @param routePath - Route path template with parameters
     * @param actualPath - Actual URL path
     * @returns Extracted parameters object
     */
    extractParamsFromPath(routePath: string, actualPath: string): RouteParamsType {
        const routeSegments = routePath.split('/');
        const pathSegments = actualPath.split('/');
        const params: RouteParamsType = {};

        for (let i = 0; i < routeSegments.length && i < pathSegments.length; i++) {
            const routeSegment = routeSegments[i];
            const pathSegment = pathSegments[i];

            if (routeSegment.startsWith(':')) {
                const paramName = routeSegment.replace(':', '').replace('?', '');
                params[paramName] = decodeURIComponent(pathSegment);
            }
        }

        return params;
    }

    /**
     * Validates if a URL matches expected format
     * @param url - URL to validate
     * @returns True if URL is valid
     */
    isValidUrl(url: string): boolean {
        try {
            new URL(url);
            return true;
        } catch {
            // Try as relative URL
            try {
                new URL(url, 'http://localhost');
                return true;
            } catch {
                return false;
            }
        }
    }

    /**
     * Normalizes a URL path by removing extra slashes and ensuring consistent format
     * @param path - Path to normalize
     * @returns Normalized path
     */
    normalizePath(path: string): string {
        if (!path) {
            return '/';
        }

        // Remove multiple consecutive slashes
        let normalized = path.replace(/\/+/g, '/');

        // Ensure path starts with /
        if (!normalized.startsWith('/')) {
            normalized = '/' + normalized;
        }

        // Remove trailing slash unless it's the root path
        if (normalized.length > 1 && normalized.endsWith('/')) {
            normalized = normalized.slice(0, -1);
        }

        return normalized;
    }

    /**
     * Logs URL parsing operations in debug mode
     * @param message - Message to log
     * @param data - Additional data to log
     */
    private debugLog(message: string, data?: unknown): void {
        if (this.debugMode) {
            console.log(`[UrlParser] ${message}`, data);
        }
    }
}
